// Temporada: calendario, classificacao e playoffs.
//
// O calendario e por RODADA -- em cada rodada todo time joga uma vez, 15 jogos
// de uma vez. Nao e o calendario da NBA (onde cada time joga em dias
// diferentes), e a simplificacao e proposital: a rodada e a unidade que o
// jogador entende, e e a unidade em que o mercado, a classificacao e o save
// avancam.
//
// A classificacao e INCREMENTAL: cada resultado atualiza a linha dos dois
// times envolvidos. Nunca se varre a temporada para montar a tabela.

import { makeRng } from './rng.js';
import { accumulateInto, makeStatLine } from './boxscore.js';
import { conferencesOf, playoffSize } from '../data/leagues.js';

/**
 * Calendario pelo metodo do circulo: os times giram em torno de um fixo, entao
 * ninguem folga e todos jogam em toda rodada. O numero de rodadas vem da LIGA
 * -- 82 na NBA, 30 no NBB (turno e returno).
 */
export function buildSchedule(teams, seed = 'cal', totalRounds = 82) {
  const rng = makeRng(`calendario|${seed}`);
  const ids = teams.map((t) => t.id);
  if (ids.length % 2 === 1) ids.push(null); // bye, se um dia a liga for impar
  const n = ids.length;
  const half = n / 2;

  const rotation = [...ids];
  const rounds = [];

  for (let r = 0; r < totalRounds; r++) {
    const games = [];
    for (let i = 0; i < half; i++) {
      const a = rotation[i];
      const b = rotation[n - 1 - i];
      if (!a || !b) continue;
      // Alterna o mando a cada volta completa, para o numero de jogos em casa
      // ficar equilibrado ao longo das 82 rodadas.
      const flip = (r + i) % 2 === 0;
      games.push({ home: flip ? a : b, away: flip ? b : a });
    }
    rounds.push(games);
    // Gira todos menos o primeiro.
    const fixed = rotation[0];
    const rest = rotation.slice(1);
    rest.unshift(rest.pop());
    rotation.splice(0, rotation.length, fixed, ...rest);
  }

  // Embaralha a ordem das rodadas para o calendario nao ser previsivel.
  for (let i = rounds.length - 1; i > 0; i--) {
    const j = rng.int(i + 1);
    [rounds[i], rounds[j]] = [rounds[j], rounds[i]];
  }
  return rounds;
}

export function emptyStandings(teams) {
  const table = {};
  for (const t of teams) {
    table[t.id] = {
      teamId: t.id,
      name: t.name,
      conference: t.conference,
      games: 0,
      wins: 0,
      losses: 0,
      pointsFor: 0,
      pointsAgainst: 0,
      streak: 0, // positivo = vitorias seguidas
      last10: [],
    };
  }
  return table;
}

/** Aplica UM resultado na tabela. O unico caminho de escrita da classificacao. */
export function applyResult(standings, result) {
  const h = standings[result.home.teamId];
  const a = standings[result.away.teamId];
  if (!h || !a) return;
  const homeWon = result.home.score > result.away.score;

  for (const [row, side, won] of [[h, result.home, homeWon], [a, result.away, !homeWon]]) {
    row.games += 1;
    row.pointsFor += side.score;
    row.pointsAgainst += side === result.home ? result.away.score : result.home.score;
    if (won) row.wins += 1; else row.losses += 1;
    row.streak = won
      ? (row.streak > 0 ? row.streak + 1 : 1)
      : (row.streak < 0 ? row.streak - 1 : -1);
    row.last10.push(won ? 1 : 0);
    if (row.last10.length > 10) row.last10.shift();
  }
}

/** Classificacao ordenada de uma conferencia (ou da liga toda). */
export function sortedStandings(standings, conference = null) {
  return Object.values(standings)
    .filter((r) => !conference || r.conference === conference)
    .sort((a, b) => {
      const pctA = a.games ? a.wins / a.games : 0;
      const pctB = b.games ? b.wins / b.games : 0;
      if (pctB !== pctA) return pctB - pctA;
      const diffA = a.pointsFor - a.pointsAgainst;
      const diffB = b.pointsFor - b.pointsAgainst;
      return diffB - diffA;
    });
}

/** Jogos atras do lider, do jeito que a NBA mostra. */
export function gamesBehind(row, leader) {
  return ((leader.wins - row.wins) + (row.losses - leader.losses)) / 2;
}

// ------------------------------------------------------------------ playoffs

/** Monta o chaveamento a partir da classificacao final, no formato da liga. */
export function buildPlayoffs(standings, config) {
  const bracket = { round: 1, series: [], champion: null };
  const conferences = conferencesOf(config);
  const perConference = playoffSize(config) / conferences.length;

  for (const conference of conferences) {
    const seeds = sortedStandings(standings, conferences.length > 1 ? conference : null)
      .slice(0, perConference);
    for (let i = 0; i < seeds.length / 2; i++) {
      const high = seeds[i];
      const low = seeds[seeds.length - 1 - i];
      bracket.series.push(makeSeries(conference, 1, high, low, i + 1, seeds.length - i));
    }
  }
  return bracket;
}

function makeSeries(conference, round, high, low, highSeed, lowSeed) {
  return {
    id: `${conference}-r${round}-${highSeed}v${lowSeed}`,
    conference,
    round,
    highSeed,
    lowSeed,
    teams: [high.teamId, low.teamId],
    names: [high.name, low.name],
    wins: [0, 0],
    games: [],
    done: false,
    winner: null,
  };
}

/** Vitorias necessarias para fechar a serie nesta fase. */
export function seriesWinsNeeded(config, round) {
  return config.seriesWins[round] ?? 4;
}

/**
 * Manda de quadra: 2-2-1-1-1 numa serie de 7 (jogos 1, 2, 5 e 7 com o melhor
 * classificado) e 2-2-1 numa de 5 (jogos 1, 2 e 5).
 */
export function seriesHomeTeam(series, gameNumber, winsNeeded = 4) {
  const highHome = winsNeeded >= 4 ? [1, 2, 5, 7] : [1, 2, 5];
  return highHome.includes(gameNumber) ? series.teams[0] : series.teams[1];
}

/** Avanca o chaveamento quando todas as series da fase terminaram. */
export function advanceBracket(bracket, standings, config) {
  const current = bracket.series.filter((s) => s.round === bracket.round);
  if (!current.length || !current.every((s) => s.done)) return bracket;

  // Uma serie so nesta fase: era a final.
  if (current.length === 1) {
    bracket.champion = current[0].winner;
    return bracket;
  }

  const nextRound = bracket.round + 1;
  const rowOf = (id) => standings[id];
  const byConference = new Map();
  for (const serie of current) {
    if (!byConference.has(serie.conference)) byConference.set(serie.conference, []);
    byConference.get(serie.conference).push({
      row: rowOf(serie.winner),
      seed: serie.winner === serie.teams[0] ? serie.highSeed : serie.lowSeed,
    });
  }

  const oneLeftPerConference = [...byConference.values()].every((v) => v.length === 1);
  if (oneLeftPerConference) {
    // Campeoes de conferencia se encontram na final.
    const finalists = [...byConference.values()].map((v) => v[0].row);
    const [high, low] = finalists.sort((a, b) => {
      const pctA = a.games ? a.wins / a.games : 0;
      const pctB = b.games ? b.wins / b.games : 0;
      return pctB - pctA;
    });
    bracket.series.push(makeSeries('Final', nextRound, high, low, 1, 2));
  } else {
    for (const [conference, winners] of byConference) {
      winners.sort((a, b) => a.seed - b.seed);
      for (let i = 0; i < winners.length / 2; i++) {
        const high = winners[i];
        const low = winners[winners.length - 1 - i];
        bracket.series.push(makeSeries(conference, nextRound, high.row, low.row, high.seed, low.seed));
      }
    }
  }
  bracket.round = nextRound;
  return bracket;
}

// --------------------------------------------------- estatistica da temporada

/**
 * Soma uma partida nos totais da temporada. INCREMENTAL: toca so os atletas
 * que jogaram. Ver `scripts/bench.mjs` para a medida de quanto isso importa.
 */
export function accumulateGame(seasonStats, result) {
  for (const side of [result.home, result.away]) {
    for (const line of side.lines) {
      if (!line.seconds) continue;
      let total = seasonStats[line.playerId];
      if (!total) {
        total = makeStatLine({
          playerId: line.playerId,
          name: line.name,
          position: line.position,
          teamId: side.teamId,
          games: 0,
        });
        seasonStats[line.playerId] = total;
      }
      accumulateInto(total, line);
      total.games += 1;
      total.teamId = side.teamId;
    }
  }
}

/** Lideres por estatistica, calculados sob demanda a partir do agregado. */
export function leaders(seasonStats, field = 'pts', minGames = 15, limit = 10) {
  // Alguns "campos" sao contas, nao colunas: rebote e a soma dos dois tipos, e
  // cesta de 2 e o que sobra das de quadra depois de tirar as de 3.
  const valorDe = (p) => {
    if (field === 'reb') return p.orb + p.drb;
    if (field === 'fgm2') return p.fgm - p.tpm;
    return p[field] ?? 0;
  };
  return Object.values(seasonStats)
    .filter((p) => p.games >= minGames)
    .map((p) => ({ ...p, per: valorDe(p) / p.games, total: valorDe(p) }))
    .sort((a, b) => b.per - a.per)
    .slice(0, limit);
}
